import { CURRENCY_METATADATA_TYPE } from '@framework/engine';
import {
  ISchema,
  ISchemaNode,
  IsStringDateMetaKey,
  Schema,
  forEachSchemaNode,
} from '@framework/validation';
import Decimal from 'decimal.js-light';
import { WidgetTypeMap } from '../../widgets/map/IMapWidget';
import { MapCadastralSuffix } from '../../widgets/map/createMapSchema';
import {
  IMetadataDirectiveSchemaMeta,
  MapMetadataType,
} from './IMetadataDirective';

export interface IExportedFieldMetadata {
  searchable: boolean;
  semanticTag: string | null | undefined;
  sectionId: string;
  widgetId: string;
  widgetType: string;
  type: string;
  code: string | undefined;
  path: string | undefined;
  value: any;
}

function getMetadataType(
  meta: Record<string, any> | null | undefined,
  directiveMetadataType: string,
) {
  if (meta?.exportedMetadataType === 'currency') {
    return 'number';
  }
  if (meta?.[IsStringDateMetaKey]) {
    return 'date';
  }
  return directiveMetadataType;
}

function parseMetadataValue(
  value: any,
  meta: Record<string, any> | null | undefined,
) {
  if (value == null) return null;
  // Nel caso delle currency, il datatype è number e il valore da esportare è di tipo numerico
  if (meta?.exportedMetadataType === CURRENCY_METATADATA_TYPE) {
    return new Decimal(value).toNumber();
  }

  return value;
}

/**
 * Esporta i valori di un modulo in base alla direttiva `metadata` dei campi.
 */
export function exportMetadataDirective(
  schema: ISchema<any>,
  options: { value: any; context?: any },
) {
  const metadatas: IExportedFieldMetadata[] = [];

  forEachSchemaNode(schema, options, node => {
    if (
      node.schema instanceof Schema &&
      node.schema.meta?.()?.directiveMetadata &&
      !node.schema.spec.strip
    ) {
      const meta = node.schema.meta?.();
      const directiveMeta =
        meta?.directiveMetadata as IMetadataDirectiveSchemaMeta | null;
      if (!directiveMeta) return;

      const [sectionId, ...pathTokens] = node.path?.split('.') ?? [];

      // 1. Caso speciale per le mappe, che richiedono un formato
      //    particolare per i metadati
      if (
        directiveMeta.widgetType === (WidgetTypeMap as any) ||
        directiveMeta.widgetType === 'multiPreference'
      ) {
        // I dati delle mappe sono sempre array; in realtà ci
        // sarebbero anche l'Area e l'Area catastale, ma non sono
        // esportati come metadati. Se servisse in futuro, si può
        // gestire
        if (!Array.isArray(node.value)) return;

        // Per ogni valore dell'array creiamo un metadato
        node.value.forEach((value, index) => {
          metadatas.push(
            buildMetadata({
              sectionId,
              pathTokens,
              node,
              meta,
              directiveMeta,
              index,
              value,
            }),
          );
        });
        return;
      }

      const metadataType = getMetadataType(meta, directiveMeta.type);

      // 2. Caso standard, per tutti gli altri campi
      metadatas.push({
        searchable: directiveMeta.searchable,
        semanticTag: directiveMeta.semanticTag,
        sectionId,
        widgetId: directiveMeta.widgetId,
        widgetType: directiveMeta.widgetType,
        type: metadataType,
        code: node.key?.toString(),
        path: pathTokens.join('.'),
        value: parseMetadataValue(node.value, meta),
      });
    }
  });

  return metadatas;
}

function isCadastralMetadata(key: string | null | undefined) {
  return Boolean(key?.endsWith(MapCadastralSuffix));
}

interface BuildMetadataInput {
  sectionId: string;
  pathTokens: string[];
  node: ISchemaNode;
  meta: Record<string, any> | null | undefined;
  directiveMeta: IMetadataDirectiveSchemaMeta;
  index: number;
  value: any;
}

function buildMetadata(input: BuildMetadataInput): IExportedFieldMetadata {
  const { sectionId, pathTokens, node, directiveMeta, index } = input;
  const widgetCode = node.key?.toString();

  return {
    searchable: false,
    semanticTag: directiveMeta.semanticTag,
    sectionId,
    widgetId: directiveMeta.widgetId,
    widgetType: directiveMeta.widgetType,
    code: `${widgetCode}.${index}`,
    path: pathTokens.join('.') + `.${index}`,
    ...buildSpecificMetadata(input),
  };
}

/**
 * Codifichiamo i metadati specifici per le mappe e i multi-preference.
 * NOTE In futuro potremmo avere anche altri tipi di metadati. Sarebbe
 * conveniente spostare questa logica in un `.meta({ metadataBuilder: () => ...})
 */
function buildSpecificMetadata(input: BuildMetadataInput) {
  const { node, directiveMeta, value } = input;
  const widgetCode = node.key?.toString();

  // Distinguiamo tra metadati di tipo Cadastrale e Geometrico, per le mappe

  if (directiveMeta.widgetType === (WidgetTypeMap as any)) {
    return {
      type: isCadastralMetadata(widgetCode)
        ? MapMetadataType.Cadastral
        : MapMetadataType.Geometry,
      value: value ?? null,
    };
  }

  if (directiveMeta.widgetType === 'multiPreference') {
    return {
      type: 'string',
      value: value?.id ?? null,
    };
  }

  // Situazione non prevista
  return {
    type: directiveMeta.type,
    value: value ?? null,
  };
}
